/**
 * `RankedBarChart` — Amendment 1 §3.3 step 9's ranked results chart: watersheds ordered by
 * yield, each row's bar sized by its own yield, with three small columns beside it for
 * NDVI (peak), rainfall and SOC — no correlation coefficient anywhere near it, since the
 * ranking itself is the whole statement (`uc3/diagnosis`'s own `statements[]`, printed
 * beside this chart, not inside it). Hand-built SVG, the same register `HistogramChart`/
 * `SeriesChart` already use (the repo forbids chart libraries, plan Global Constraints).
 *
 * Fix round 1: the `aria-label` readout carries every number with its own unit (kg/ha, NDVI,
 * mm, g/kg — `uc3/diagnosis`'s own factor units) and the run's season/year, per the
 * presentation standard's "numbers always with unit and period".
 */
import { useRef } from 'react';

import { colonSep, fmtNum } from '../../../lib/format';
import type { IntelDiagnosisRankedRow } from '../../../lib/api';
import { AxisLabels } from '../../../components/charts/AxisLabels';
import { useAxisTitles } from '../../../components/charts/axisContext';
import { axisBands } from '../../../components/charts/scale';
import { LABEL_LINE_H, VALUE_GAP, VALUE_HALO, labelColumn } from '../../../components/charts/labels';
import { RowLabel } from '../../../components/charts/RowLabel';
import { useChartMarks } from '../../../components/charts/useChartMarks';
import { chartFont, textWidth, wrapText } from '../../../lib/text';
import { COMPACT_W, ROW_LABEL_PX, valueWidth } from './labels';
import { useIntelWidth } from './layout';

/** The row name's size (`.rb-rowlabel`), which wrapping measures at. */
const RB_LABEL_SIZE = ROW_LABEL_PX;
/** The mini-columns' header size. */
const RB_HEAD_SIZE = 9;

/** The width the chart is laid out at where its box cannot be measured (#749: it measures). */
export const RB_W = 360;
export const RB_ROW_H = 22;
export const RB_ROW_GAP = 10;
/** The label column's minimum; it grows to the longest name. */
export const RB_LABEL_W = 96;
/** The yield track's minimum; it takes whatever the other columns leave. */
export const RB_YIELD_W = 120;
/** A mini-column's minimum width; it grows to its header's longest word. */
export const RB_COL_W = 34;
export const RB_COL_GAP = 6;
export const RB_PAD_TOP = 8;
export const RB_MIN_H = 60;

export interface RankedBarChartProps {
  rows: IntelDiagnosisRankedRow[];
  /** The run's own period — `uc3/diagnosis.season`/`.year` — read into the aria readout. */
  season?: string;
  year?: number;
  yieldLabel: string;
  /** GAEZ round: the modelled-yield column — "modelled (FAO GAEZ v5, 2020)". */
  modelledLabel: string;
  ndviLabel: string;
  rainLabel: string;
  socLabel: string;
  /** `uc3/diagnosis`'s own factor units — default to the values the live payload carries. */
  yieldUnit?: string;
  modelledUnit?: string;
  ndviUnit?: string;
  rainUnit?: string;
  socUnit?: string;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** The x axis' title, "quantity (unit)", centred under the chart (launch round). */
  xLabel?: string;
  /** The y axis' title, rotated at the left of the label column. */
  yLabel?: string;
  /** Layout width in px; measured from the chart's own box when omitted (#749). */
  w?: number;
}

function maxOf(
  rows: IntelDiagnosisRankedRow[],
  key: 'yield' | 'modelled_yield' | 'ndvi_peak' | 'rain' | 'soc',
): number {
  const values = rows.map((r) => r[key]).filter((v): v is number => typeof v === 'number');
  return values.length ? Math.max(...values) : 1;
}

/**
 * The chart's own `aria-label` — a plain-text readout of every row (each number with its own
 * unit) plus the run's period, for §I5 and the test list.
 */
function readout(
  rows: IntelDiagnosisRankedRow[],
  labels: { yield: string; modelled: string; ndvi: string; rain: string; soc: string },
  units: { yield: string; modelled: string; ndvi: string; rain: string; soc: string },
  season?: string,
  year?: number,
): string {
  const withUnit = (v: number | null, unit: string, digits: number) =>
    v == null ? '-' : `${fmtNum(v, digits)} ${unit}`;
  const period = season || year ? `${[season, year].filter(Boolean).join(', ')}. ` : '';
  const rowLines = rows
    .map(
      (row) =>
        `${row.rank}. ${row.name}: ${labels.yield} ${withUnit(row.yield, units.yield, 0)}, ` +
        `${labels.modelled} ${withUnit(row.modelled_yield, units.modelled, 1)}, ` +
        `${labels.ndvi} ${withUnit(row.ndvi_peak, units.ndvi, 2)}, ` +
        `${labels.rain} ${withUnit(row.rain, units.rain, 0)}, ` +
        `${labels.soc} ${withUnit(row.soc, units.soc, 1)}`,
    )
    .join('. ');
  return `${period}${rowLines}`;
}

/**
 * The mini-columns' width and headers. Each header is wrapped onto as many lines as its
 * column needs (never cut), and the column is at least as wide as the header's longest
 * word, so two headers can no longer run into each other above the rows: the 7 px headers
 * of the launch round, all on one line at a fixed 34-unit pitch, did exactly that.
 */
function headerLayout(labels: readonly string[]): { colW: number; lines: string[][]; height: number } {
  const font = chartFont(RB_HEAD_SIZE);
  const longestWord = labels
    .flatMap((l) => l.split(/\s+/))
    .reduce((m, word) => Math.max(m, textWidth(word, RB_HEAD_SIZE)), 0);
  const colW = Math.min(72, Math.max(RB_COL_W, Math.ceil(longestWord + 4)));
  const lines = labels.map((l) => wrapText(l, colW, font, 3));
  const most = Math.max(1, ...lines.map((l) => l.length));
  return { colW, lines, height: Math.ceil(most * RB_HEAD_SIZE * LABEL_LINE_H + 6) };
}

export function RankedBarChart({
  rows,
  season,
  year,
  yieldLabel,
  modelledLabel,
  ndviLabel,
  rainLabel,
  socLabel,
  yieldUnit = 'kg/ha',
  modelledUnit = 't/ha',
  ndviUnit = 'NDVI',
  rainUnit = 'mm',
  socUnit = 'g/kg',
  selectedId,
  onSelect,
  xLabel: xLabelProp,
  yLabel: yLabelProp,
  w: wProp,
}: RankedBarChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { xLabel, yLabel } = useAxisTitles(xLabelProp, yLabelProp);
  const bands = axisBands(xLabel, yLabel);
  const outerW = useIntelWidth(svgRef, wProp, RB_W + bands.left);
  const W = outerW - bands.left;
  const marks = useChartMarks<string>();
  /** Below `COMPACT_W` the four mini-columns are left out; the popup still carries them. */
  const compact = outerW < COMPACT_W;
  const maxYield = maxOf(rows, 'yield');
  const cols: {
    key: 'modelled_yield' | 'ndvi_peak' | 'rain' | 'soc';
    max: number;
    label: string;
    unit: string;
    digits: number;
    color: string;
  }[] = [
    { key: 'modelled_yield', max: maxOf(rows, 'modelled_yield'), label: modelledLabel, unit: modelledUnit, digits: 1, color: 'var(--plum, var(--ochre))' },
    // Part C (11 Oct 2026): `uc3/diagnosis` serves season-peak NDVI in true units (0.42).
    { key: 'ndvi_peak', max: maxOf(rows, 'ndvi_peak'), label: ndviLabel, unit: ndviUnit, digits: 2, color: 'var(--brand)' },
    { key: 'rain', max: maxOf(rows, 'rain'), label: rainLabel, unit: rainUnit, digits: 0, color: 'var(--blue)' },
    { key: 'soc', max: maxOf(rows, 'soc'), label: socLabel, unit: socUnit, digits: 1, color: 'var(--ochre)' },
  ];
  const head = headerLayout(cols.map((c) => c.label));
  const colsW = compact ? 0 : cols.length * head.colW + (cols.length - 1) * RB_COL_GAP;
  // A long name wraps onto a second line and its row grows by that line (launch round,
  // 7 Oct 2026): never cut, never running off the left edge.
  const names = rows.map((row) => `${row.rank}. ${row.name}`);
  const column = labelColumn(names, { size: RB_LABEL_SIZE, min: Math.min(RB_LABEL_W, Math.round(W * 0.3)), total: W * 0.7 });
  const plotX = column.width;
  const yieldTexts = rows.map((row) => (row.yield == null ? '-' : fmtNum(row.yield, 0)));
  const valueW = Math.ceil(Math.max(0, ...yieldTexts.map((t) => valueWidth(t))));
  const trackW = Math.max(40, W - plotX - VALUE_GAP - valueW - (compact ? 4 : 14) - colsW);
  const colX0 = plotX + trackW + VALUE_GAP + valueW + 14;
  const colX = (ci: number) => colX0 + ci * (head.colW + RB_COL_GAP);
  const top = RB_PAD_TOP + (compact ? 0 : head.height);
  const lines = column.lines;
  const extras = lines.map((l) => Math.max(0, l.length * RB_LABEL_SIZE * LABEL_LINE_H - RB_ROW_H + 2));
  const pitch = RB_ROW_H + RB_ROW_GAP;
  const tops = extras.map((_, i) => top + i * pitch + extras.slice(0, i).reduce((a, b) => a + b, 0));
  const h = Math.max(RB_MIN_H, top + Math.max(rows.length, 1) * pitch + extras.reduce((a, b) => a + b, 0));
  const withUnit = (v: number | null, unit: string, digits: number) =>
    v == null ? '-' : `${fmtNum(v, digits)} ${unit}`;

  return (
    <svg
      ref={svgRef}
      className={`intel-chart rb-chart${marks.rootClass}`}
      width="100%"
      viewBox={`0 0 ${outerW} ${h + bands.bottom}`}
      role="img"
      aria-label={readout(
        rows,
        { yield: yieldLabel, modelled: modelledLabel, ndvi: ndviLabel, rain: rainLabel, soc: socLabel },
        { yield: yieldUnit, modelled: modelledUnit, ndvi: ndviUnit, rain: rainUnit, soc: socUnit },
        season,
        year,
      )}
      data-testid="ranked-bar-chart"
      data-fluid=""
      data-w={outerW}
      data-compact={compact ? '' : undefined}
      onMouseLeave={marks.clear}
    >
      <AxisLabels
        xLabel={xLabel}
        yLabel={yLabel}
        plotLeft={bands.left + plotX}
        plotRight={bands.left + plotX + trackW}
        plotTop={top}
        plotBottom={h}
        height={h + bands.bottom}
      />
      <g transform={bands.left ? `translate(${bands.left} 0)` : undefined}>
      {/* Column headers, once, in a band of their own above the first row. */}
      {compact
        ? null
        : cols.map((col, ci) => (
            <text
              key={col.key}
              x={colX(ci)}
              y={RB_PAD_TOP + RB_HEAD_SIZE - 2}
              className="rb-collabel"
              fontSize={RB_HEAD_SIZE}
              data-col={col.key}
            >
              {(head.lines[ci] ?? [col.label]).map((line, li) => (
                <tspan key={li} x={colX(ci)} dy={li === 0 ? 0 : RB_HEAD_SIZE * LABEL_LINE_H}>
                  {line}
                </tspan>
              ))}
            </text>
          ))}
      <g role="list">
      {rows.map((row, i) => {
        const y = tops[i]! + extras[i]! / 2;
        const w = row.yield == null ? 0 : Math.max(2, (row.yield / maxYield) * trackW);
        const selected = row.unit_id === selectedId;
        return (
          <g
            key={row.unit_id}
            className={`rb-row mark${marks.isHot(row.unit_id) ? ' is-hot' : ''}`}
            data-unit={row.unit_id}
            {...marks.markProps(
              row.unit_id,
              () => ({
                label: `${row.rank}. ${row.name}`,
                value: `${yieldLabel} ${withUnit(row.yield, yieldUnit, 0)}`,
                rowsOnly: true,
                rows: [
                  { label: yieldLabel, value: withUnit(row.yield, yieldUnit, 0), color: 'var(--brand-dark, var(--brand))' },
                  ...cols.map((col) => ({ label: col.label, value: withUnit(row[col.key], col.unit, col.digits), color: col.color })),
                ],
              }),
              onSelect ? () => onSelect(row.unit_id) : undefined,
            )}
          >
            <title>
              {`${row.rank}. ${row.name}${colonSep()}${yieldLabel} ${row.yield == null ? '-' : `${fmtNum(row.yield, 0)} ${yieldUnit}`}`}
            </title>
            <RowLabel
              className="rb-rowlabel"
              lines={lines[i]!}
              x={plotX - 8}
              baseline={y + RB_ROW_H / 2 + 4}
              size={RB_LABEL_SIZE}
              textAnchor="end"
            />
            {w > 0 ? (
              <rect
                className="mk"
                x={plotX}
                y={y}
                width={w}
                height={RB_ROW_H}
                rx={3}
                fill="var(--brand-dark, var(--brand))"
                opacity={selected ? 1 : 0.78}
                stroke={selected ? 'var(--ink)' : 'none'}
                strokeWidth={selected ? 1.5 : 0}
              />
            ) : null}
            <text className="rb-num" x={plotX + w + VALUE_GAP} y={y + RB_ROW_H / 2 + 4} style={VALUE_HALO}>
              {yieldTexts[i]}
            </text>
            {compact
              ? null
              : cols.map((col, ci) => {
                  const v = row[col.key];
                  const barW = v == null ? 0 : Math.max(2, (v / col.max) * head.colW);
                  return (
                    <g key={col.key} data-col={col.key}>
                      {barW > 0 ? (
                        <rect x={colX(ci)} y={y + 3} width={barW} height={RB_ROW_H - 6} rx={2} fill={col.color} opacity={0.7} />
                      ) : null}
                    </g>
                  );
                })}
          </g>
        );
      })}
      </g>
      </g>
    </svg>
  );
}

export default RankedBarChart;
